import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource/schibsted-grotesk/700.css";
import "@fontsource/schibsted-grotesk/800.css";
import "./styles.css";
import "./plan/plan.css";
import "./home/records.css";
import { calendarDateAt, planWeek } from "@hitl/rotations-domain";
import { createDemoTenant } from "./demo/demoTenant";
import { createEventLog, demoTransport } from "./state/eventLog";
import { createPlannerStore } from "./state/plannerStore";
import { createPersistedStore } from "./state/persistedStore";
import type { SettingsState } from "./state/settingsState";
import { markLoaded, pinPot, placeRoute, upcomingRouteDays } from "./state/plannerState";
import { deriveDay, placeCommand } from "./state/session";
import { App, type View } from "./App";
import type { Fixture } from "./demo/fixtures";

const today = calendarDateAt(new Date());
const demo = createDemoTenant(today);

let fixture: Fixture | undefined;
if (import.meta.env.DEV) {
  const name = new URLSearchParams(location.search).get("fixture");
  if (name) fixture = (await import("./demo/fixtures")).FIXTURES[name];
}

// Fixtures get throwaway stores so they never touch the demo you're clicking through.
const scope = fixture ? `fixture:${Math.random()}` : `demo:${today}`;
const log = createEventLog({
  storageKey: `rotations:${scope}`,
  seedEvents: demo.seedEvents,
  recordedBy: () => settings.get().people.current,
  transport: demoTransport,
  isOnline: () => navigator.onLine,
});
const settings = createPersistedStore<SettingsState>(`rotations:settings:${fixture ? scope : "demo"}`, () => demo.settings);
const planner = createPlannerStore(`rotations:planner:${fixture ? scope : "demo"}`, () =>
  fixture?.noRouteToday ? placeRoute(demo.planner, today, null) : demo.planner,
);
if (fixture?.pinTwice) {
  const route = upcomingRouteDays(planner.get(), today).find((d) => d.on === today)?.route;
  if (route) planner.update((s) => pinPot(pinPot(s, today, route.slotIds[0]!, fixture!.pinTwice!), today, route.slotIds[1]!, fixture!.pinTwice!));
}
if (fixture?.load?.lastIn) planner.update((s) => markLoaded(s, today, fixture!.load!.lastIn!));

// Fixture: record today's planned swaps for the first N spots (or all) before rendering.
if (fixture?.completeDay || fixture?.completeFirst) {
  const route = upcomingRouteDays(planner.get(), today).find((d) => d.on === today)?.route;
  if (route) {
    const { catalog, policy, historyKnownSince } = demo.setup;
    const plan = planWeek([{ on: today, slotIds: route.slotIds }], { catalog, realEvents: log.all(), policy, historyKnownSince }).days[0]!.plan;
    const setup = { catalog, policy, historyKnownSince, route: { on: today, slotIds: route.slotIds }, plan };
    const count = fixture.completeDay ? route.slotIds.length : fixture.completeFirst!;
    for (let i = 0; i < count; i++) {
      const day = deriveDay(setup, log.all());
      const spot = day.spots[i]!;
      log.append(placeCommand(day, spot, spot.plannedPotId!, today));
    }
  }
}

window.addEventListener("online", () => void log.sync());
window.addEventListener("offline", () => void log.sync());

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  void navigator.serviceWorker.register("/sw.js");
}

// The app opens on Home. Older fixtures without a view open on Today (the scan screen).
const initialView: View | undefined = !fixture
  ? undefined
  : fixture.view === "plan"
    ? { kind: "plan" }
    : fixture.view === "load"
      ? { kind: "load", on: today, from: "plan" }
      : fixture.view === "reports"
        ? { kind: "reports" }
        : fixture.view === "settings"
          ? { kind: "settings" }
        : fixture.view === "home"
          ? { kind: "home" }
          : { kind: "today" };

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App demo={demo} log={log} planner={planner} settings={settings} today={today} {...(fixture ? { fixture } : {})} {...(initialView ? { initialView } : {})} />
  </StrictMode>,
);
